import type { Key } from 'react';
import { useSearchParams } from 'react-router';
import { updateURLSearchParamsWithProjectContext } from '@gonfalon/app-context/updateURLSearchParamsWithProjectContext';
import { enableCachedLoader } from '@gonfalon/dogfood-flags/dogfood-flags';
import { putSelectedEnvironments } from '@gonfalon/openapi/operations/putSelectedEnvironments';
import { internalEnvironmentList } from '@gonfalon/rest-api/queries/environments-options';
import { selectedEnvironments } from '@gonfalon/rest-api/queries/selectedEnvironments';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { type SelectedEnvironment } from '../types';

export const useEnvironmentController = ({
  projectKey,
  filterSearchParams,
}: {
  projectKey: string;
  filterSearchParams?: ({ params }: { params: URLSearchParams }) => URLSearchParams;
}) => {
  const selectedEnvironmentsQuery = selectedEnvironments({ projectKey });
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: { environments, selectedEnvironmentKey: apiSelectedEnvironmentKey },
  } = useSuspenseQuery(selectedEnvironmentsQuery);
  const selectedEnvironmentKey = searchParams.get('selected-env') ?? apiSelectedEnvironmentKey;

  const queryClient = useQueryClient();

  const addEnvironment = (environment: SelectedEnvironment) => {
    const nextList = [...environments, environment];

    setEnvironments(nextList, environment.key);
  };
  const removeEnvironment = (key: Key) => {
    const removedIndex = environments.findIndex((it) => it.key === key);

    let nextSelectedKey = selectedEnvironmentKey;
    if (selectedEnvironmentKey === key) {
      const previousEnvironment = environments.at(Math.abs(removedIndex - 1)) ?? environments[0];
      nextSelectedKey = previousEnvironment.key;
    }

    const nextList = environments.filter((it) => it.key !== key);

    setEnvironments(nextList, nextSelectedKey);
  };
  const replaceEnvironment = (sourceEnvironmentKey: Key, targetEnvironment: SelectedEnvironment) => {
    const sourceIndex = environments.findIndex((it) => it.key === sourceEnvironmentKey);

    let nextSelectedKey = selectedEnvironmentKey;
    if (environments[sourceIndex].key === selectedEnvironmentKey) {
      nextSelectedKey = targetEnvironment.key;
    }

    const nextList = [...environments];
    nextList[sourceIndex] = targetEnvironment;

    setEnvironments(nextList, nextSelectedKey);
  };

  const setEnvironments = (updatedEnvironments: SelectedEnvironment[], updatedSelectedEnvironmentKey: string) => {
    let params = searchParams;
    if (filterSearchParams) {
      params = filterSearchParams({ params: searchParams });
    }

    const nextData = {
      projectKey,
      environmentKeys: new Set(updatedEnvironments.map((env) => env.key)),
      selectedEnvironmentKey: updatedSelectedEnvironmentKey,
    };
    const nextParams = updateURLSearchParamsWithProjectContext(params, nextData);

    if (enableCachedLoader()) {
      const oldData = queryClient.getQueryData(
        internalEnvironmentList({
          projectKey: nextData.projectKey,
          query: {
            key: Array.from(new Set(environments.map((env) => env.key))).sort(),
          },
        }).queryKey,
      );

      queryClient.setQueryData(
        internalEnvironmentList({
          projectKey: nextData.projectKey,
          query: {
            key: Array.from(nextData.environmentKeys).sort(),
          },
        }).queryKey,
        () => ({
          ...oldData,
          totalCount: nextData.environmentKeys.size,
          items: [...updatedEnvironments],
        }),
      );

      queryClient.setQueryData(selectedEnvironmentsQuery.queryKey, (old) => ({
        ...old,
        environments: updatedEnvironments,
        selectedEnvironmentKey: nextData.selectedEnvironmentKey,
      }));
    }

    const syncMessage = {
      projectKey,
      body: {
        environmentKeys: updatedEnvironments.map((e) => e.key),
        selectedEnvironmentKey: nextData.selectedEnvironmentKey,
      },
    };

    void putSelectedEnvironments(syncMessage);

    setSearchParams(nextParams, { state: { nextList: updatedEnvironments } });
  };

  return {
    addEnvironment,
    removeEnvironment,
    replaceEnvironment,
    setEnvironments,
    environments,
    selectedEnvironmentKey,
  };
};
